import {
  CButton,
  COUNTRIES,
  FormGroup,
  disableButton,
  handleNavigationStatus,
} from '@maw/utility';
import {
  ActionFunction,
  ActionFunctionArgs,
  Form,
  LoaderFunction,
  LoaderFunctionArgs,
  useActionData,
  useNavigation,
  useParams,
} from 'react-router-dom';
import Card from '../../../Card';
import { getById, queryClient, upsertResource } from '../../../../util/request';
import Container from '../../../Container';
import Alert from '../../../Alert';
import { useRedirect } from '../../../../hooks/redirect';
import { postStatus } from '../../../../util/constant';
import { useQuery } from '@tanstack/react-query';
import HiddenInput from '../../../HiddenInput';
import Modal from '../../../Modal';
import useOperation from '../../../../hooks/operation';
import { disableWhenLoading } from '../../../../util/helper';
import LogoCard from '../../../LogoCard';

export default function CustomerDetails() {
  const navigation = useNavigation();
  const params = useParams();
  const { id, show, handleDelete, markObjectForDeletion, closeModal } =
    useOperation();
  const { data } = useQuery({
    queryKey: ['customers', params.id],
    queryFn: ({ signal }) =>
      getById({ signal, resource: `customers/${params.id}` }),
  });

  const actionData: any = useActionData();
  useRedirect(actionData, 'customers', true);
  const customer: Customer = data?.data;

  return (
    <Container>
      <Modal
        show={show}
        onClose={closeModal}
        onAction={() =>
          handleDelete(`customers/${id}/images/delete`, ['customers', id])
        }
      />
      <Alert data={actionData} />
      <Container.Heading>Customer Details</Container.Heading>
      <Container.SubHeading>
        Authorized user can write the file.
      </Container.SubHeading>
      {customer && customer.logo && (
        <LogoCard
          className='my-4'
          companyName={customer.companyName}
          id={customer.id}
          onDelete={() => markObjectForDeletion(customer.id)}
          allowDelete
          key={customer.id}
          logo={customer.logo}
        />
      )}
      {customer && (
        <Card>
          <Card.Header>Customer Details</Card.Header>
          {customer && (
            <Form method='post' encType='multipart/form-data' noValidate>
              <FormGroup>
                <div className='flex flex-col gap-x-2 md:flex-row md:space-x-2'>
                  <FormGroup.Input
                    labelclassname='bg-gray-100'
                    className='flex-1'
                    type='text'
                    id='contact_person'
                    title='Contact person'
                    placeholder='Enter contact person'
                    defaultValue={customer.contactPerson}
                    data={actionData}
                    disabled={disableWhenLoading(navigation)}
                  />
                  <FormGroup.Input
                    labelclassname='bg-gray-100'
                    className='flex-1'
                    type='text'
                    id='company_name'
                    title='Company name'
                    placeholder='Enter company name'
                    defaultValue={customer.companyName}
                    data={actionData}
                    disabled={disableWhenLoading(navigation)}
                  />
                </div>
                <div className='flex flex-col gap-x-2 md:flex-row md:space-x-2'>
                  <FormGroup.Input
                    labelclassname='bg-gray-100'
                    className='flex-1'
                    type='email'
                    id='email'
                    title='Email'
                    placeholder='Enter email'
                    defaultValue={customer.email}
                    data={actionData}
                    disabled={disableWhenLoading(navigation)}
                  />
                  <FormGroup.Input
                    labelclassname='bg-gray-100'
                    className='flex-1'
                    type='phone'
                    id='telephone'
                    title='Telephone'
                    placeholder='Enter telephone'
                    defaultValue={customer.telephone}
                    data={actionData}
                    disabled={disableWhenLoading(navigation)}
                  />
                </div>
                <div className='flex flex-col gap-x-2 md:flex-row md:space-x-2'>
                  <FormGroup.Input
                    labelclassname='bg-gray-100'
                    className='flex-1'
                    type='text'
                    id='address'
                    title='Address'
                    placeholder='Enter address'
                    defaultValue={customer.address}
                    data={actionData}
                    disabled={disableWhenLoading(navigation)}
                  />
                </div>
                <div className='flex flex-col gap-x-2 md:flex-row md:space-x-2'>
                  <FormGroup.Input
                    labelclassname='bg-gray-100'
                    className='flex-1'
                    type='file'
                    id='logo'
                    title='Logo (MaxHeight: 500, MaxWidth: 500)'
                    accept='.jpeg, .png, .jpg, .svg'
                    data={actionData}
                    disabled={disableWhenLoading(navigation)}
                  />
                  <FormGroup.Input
                    labelclassname='bg-gray-100'
                    className='flex-1'
                    type='text'
                    id='postal_code'
                    title='Postal code'
                    placeholder='Enter postal code'
                    defaultValue={customer.postalCode}
                    data={actionData}
                    disabled={disableWhenLoading(navigation)}
                  />
                </div>
                <div className='flex flex-col gap-x-2 md:flex-row md:space-x-2'>
                  <FormGroup.Input
                    labelclassname='bg-gray-100'
                    className='flex-1'
                    type='text'
                    id='business_number'
                    title='Business number'
                    placeholder='Enter business number'
                    defaultValue={customer.businessNumber}
                    data={actionData}
                    disabled={disableWhenLoading(navigation)}
                  />
                  <FormGroup.Input
                    labelclassname='bg-gray-100'
                    className='flex-1'
                    type='text'
                    id='city'
                    title='City'
                    placeholder='Enter city'
                    defaultValue={customer.city}
                    data={actionData}
                    disabled={disableWhenLoading(navigation)}
                  />
                </div>
                <div className='flex flex-col gap-x-2 md:flex-row md:space-x-2'>
                  <FormGroup.Select
                    labelclassname='bg-gray-100'
                    className='flex-1'
                    id='post_status'
                    title='Post status'
                    defaultValue={customer.postStatus}
                    data={actionData}
                    disabled={disableWhenLoading(navigation)}
                  >
                    {postStatus.map((status) => (
                      <option value={status.value} key={status.value}>
                        {status.name}
                      </option>
                    ))}
                  </FormGroup.Select>
                  <FormGroup.Select
                    labelclassname='bg-gray-100'
                    className='flex-1'
                    id='country_of_operation'
                    title='Country of operation'
                    defaultValue={customer.countryOfOperation}
                    data={actionData}
                    disabled={disableWhenLoading(navigation)}
                  >
                    {COUNTRIES.map((country) => (
                      <option value={country.value} key={country.value}>
                        {country.value}
                      </option>
                    ))}
                  </FormGroup.Select>
                </div>
                <HiddenInput />
                <CButton
                  rounded
                  variant='primary'
                  type='submit'
                  disabled={disableButton(navigation)}
                  className='mt-6'
                >
                  {handleNavigationStatus(navigation, 'Submit')}
                </CButton>
              </FormGroup>
            </Form>
          )}
        </Card>
      )}
    </Container>
  );
}

export async function loader({
  params,
}: LoaderFunctionArgs): Promise<LoaderFunction> {
  return await queryClient.fetchQuery({
    queryKey: ['customers', params.id],
    queryFn: ({ signal }) =>
      getById({ signal, resource: `customers/${params.id}` }),
  });
}

export async function action({
  request,
  params,
}: ActionFunctionArgs): Promise<ActionFunction> {
  await queryClient.invalidateQueries({ queryKey: ['customers'] });
  return await upsertResource({
    request,
    resource: `customers/${params.id}`,
    isFormData: true,
    action: 'update',
  });
}
